import React, { useState, useEffect } from 'react';
import { 
  FaDollarSign, 
  FaChartLine, 
  FaCalendarAlt, 
  FaFilter, 
  FaDownload, 
  FaEye, 
  FaSpinner,
  FaArrowUp,
  FaArrowDown,
  FaUsers,
  FaBriefcase,
  FaClock
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { earningsService } from '../../api/earningsService';
import DriverEarningsModal from '../../components/admin/DriverEarningsModal';

const AdminEarnings = () => {
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [filters, setFilters] = useState({
    period: 'week',
    driverId: '',
    startDate: '',
    endDate: ''
  });
  const [summary, setSummary] = useState({
    totalEarnings: 0,
    totalJobs: 0,
    averagePerJob: 0
  });
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  useEffect(() => {
    fetchEarnings();
    fetchAnalytics();
  }, [filters, selectedPeriod]);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const response = await earningsService.getAllEarnings(filters);
      
      if (response.success) {
        setEarnings(response.data.earnings || []);
        setSummary(response.data.summary || {});
      } else {
        toast.error(response.message || 'Failed to load earnings');
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await earningsService.getEarningsAnalytics({
        period: selectedPeriod,
        startDate: filters.startDate,
        endDate: filters.endDate
      });
      
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setFilters(prev => ({ ...prev, period }));
  };

  const clearFilters = () => {
    setFilters({
      period: 'week',
      driverId: '',
      startDate: '',
      endDate: ''
    });
    setSelectedPeriod('week');
  };

  const exportEarnings = () => {
    // Create CSV data
    const csvContent = [
      ['Driver', 'Total Earnings', 'Jobs Completed', 'Period', 'Date'],
      ...earnings.map(earning => [
        earning.driverName || 'Unknown',
        `$${earning.totalPay || 0}`,
        earning.jobsCompleted || 1,
        filters.period,
        new Date(earning.createdAt?.toDate?.() || earning.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `earnings-${filters.period}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Earnings exported successfully');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getGrowthIcon = (growthRate) => {
    if (growthRate > 0) {
      return <FaArrowUp className="text-green-500" />;
    } else if (growthRate < 0) {
      return <FaArrowDown className="text-red-500" />;
    }
    return null;
  };

  const handleViewDriverEarnings = (driverId, driverName) => {
    setSelectedDriver({ id: driverId, name: driverName });
    setShowDriverModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin h-12 w-12 text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Earnings Management</h1>
            <p className="text-gray-600">Track driver earnings and payments</p>
          </div>
          {/* <div className="flex space-x-3">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <FaChartLine className="mr-2" />
              {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
            </button>
            <button
              onClick={exportEarnings}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
            >
              <FaDownload className="mr-2" />
              Export
            </button>
          </div> */}
        </div>
      </div>

      {/* Summary Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FaDollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Earnings</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalEarnings)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <FaBriefcase className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <FaChartLine className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Average Per Job</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.averagePerJob)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <FaUsers className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Drivers</p>
              <p className="text-2xl font-bold text-gray-900">{earnings.length}</p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Analytics Section */}
      {showAnalytics && analytics && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Earnings Analytics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Growth Rate</p>
              <div className="flex items-center justify-center space-x-2">
                {getGrowthIcon(analytics.summary.growthRate)}
                <p className={`text-2xl font-bold ${
                  analytics.summary.growthRate > 0 ? 'text-green-600' : 
                  analytics.summary.growthRate < 0 ? 'text-red-600' : 'text-gray-900'
                }`}>
                  {analytics.summary.growthRate > 0 ? '+' : ''}{analytics.summary.growthRate}%
                </p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Period</p>
              <p className="text-2xl font-bold text-gray-900 capitalize">{analytics.trends.period}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Data Points</p>
              <p className="text-2xl font-bold text-gray-900">{Object.keys(analytics.trends.dailyEarnings).length}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <FaFilter className="text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          <div className="flex space-x-4">
            <select
              value={filters.period}
              onChange={(e) => handleFilterChange('period', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>

            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Start Date"
            />

            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="End Date"
            />

            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Period Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {['week', 'month', 'quarter', 'year'].map((period) => (
              <button
                key={period}
                onClick={() => handlePeriodChange(period)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedPeriod === period
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Earnings Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Driver
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Earnings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jobs Completed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Average Per Job
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th> */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {earnings.length > 0 ? (
                earnings.map((earning) => (
                  <tr key={earning.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {(earning.driverName || earning.driverId || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {earning.driverName || `Driver ${earning.driverId}`}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {earning.driverId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(earning.totalPay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {earning.jobsCompleted || 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(earning.totalPay / (earning.jobsCompleted || 1))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {earning.createdAt ? 
                        new Date(earning.createdAt.toDate ? earning.createdAt.toDate() : earning.createdAt).toLocaleDateString() : 
                        'N/A'
                      }
                    </td>
                    {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewDriverEarnings(earning.driverId, earning.driverName)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Driver Earnings Details"
                      >
                        <FaEye className="h-4 w-4" />
                      </button>
                    </td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <FaDollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-lg font-medium">No earnings data found</p>
                      <p className="text-sm">Try adjusting your filters or check back later</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Driver Earnings Modal */}
      <DriverEarningsModal
        isOpen={showDriverModal}
        onClose={() => {
          setShowDriverModal(false);
          setSelectedDriver(null);
        }}
        driverId={selectedDriver?.id}
        driverName={selectedDriver?.name}
      />
    </div>
  );
};

export default AdminEarnings; 